import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ExpansionPanel, ExpansionPanelList, ExpansionPanelListItem } from '../src/components/expansion-panel';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <ExpansionPanel title="How to configure an Apollo project" mdxType="ExpansionPanel">
      <ExpansionPanelList mdxType="ExpansionPanelList">
        <ExpansionPanelListItem number="1" mdxType="ExpansionPanelListItem">
          <h4>Install the Apollo CLI</h4>
          <p>{`Ensure that `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`node`}</code>{` and `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`npm`}</code>{` are both installed, then run:`}</p>
          <div {...{
            "className": "gatsby-highlight",
            "data-language": "bash"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-bash line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-bash"
              }}><span parentName="code" {...{
                  "className": "token function"
                }}>{`npm`}</span>{` `}<span parentName="code" {...{
                  "className": "token function"
                }}>{`install`}</span>{` --global apollo`}</code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span></span></pre></div>
          <p>{`This installs the CLI globally, but you can also install the CLI in your project's `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`devDependencies`}</code>{` and use it via `}<a parentName="p" {...{
              "href": "https://docs.npmjs.com/misc/scripts"
            }}><code parentName="a" {...{
                "className": "language-text"
              }}>{`npm-scripts`}</code></a>{` or `}<a parentName="p" {...{
              "href": "https://npm.im/npx"
            }}><code parentName="a" {...{
                "className": "language-text"
              }}>{`npx`}</code></a>{`.`}</p>
        </ExpansionPanelListItem>
        <ExpansionPanelListItem number="2" mdxType="ExpansionPanelListItem">
          <h4>Obtain a personal API key</h4>
          <p>{`If you haven't yet, `}<a parentName="p" {...{
              "href": "https://studio.apollographql.com"
            }}>{`log in to Apollo Studio`}</a>{` and create a graph for your organization by clicking `}<strong parentName="p">{`New Graph`}</strong>{` in the upper right.`}</p>
          <p>{`Then go to your `}<a parentName="p" {...{
              "href": "https://studio.apollographql.com/user-settings"
            }}>{`Personal Settings page`}</a>{` and obtain or create a new personal API key.`}</p>
          <p>{`When you have your API key, add it to a `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`.env`}</code>{` file in the root directory of your project, like so:`}</p>
          <div {...{
            "className": "gatsby-code-title"
          }}>{`.env`}</div>
          <div {...{
            "className": "gatsby-highlight",
            "data-language": "none"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-none line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-none"
              }}>{`APOLLO_KEY=user:gh.StephenBarlow:uB0sLhga7EemND1wTa6BcZ`}</code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span></span></pre></div>
          <p><strong parentName="p">{`Do not add the `}<code parentName="strong" {...{
                "className": "language-text"
              }}>{`.env`}</code>{` file to version control.`}</strong>{` It contains secrets that differ both per user and per environment.`}</p>
          <p>{`When you execute Apollo CLI commands (such as to register a schema), the CLI obtains your API key from the `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`.env`}</code>{` file and uses it to authenticate with Studio.`}</p>
        </ExpansionPanelListItem>
        <ExpansionPanelListItem number="3" mdxType="ExpansionPanelListItem">
          <h4>Create an apollo.config.js file</h4>
          <p>{`Also in your project's root directory, create a file named `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`apollo.config.js`}</code>{`. The Apollo CLI uses configuration options in this file during the execution of various commands.`}</p>
          <p>{`A basic `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`apollo.config.js`}</code>{` file looks like this:`}</p>
          <div {...{
            "className": "gatsby-highlight",
            "data-language": "js"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-js line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-js"
              }}><span parentName="code" {...{
                  "className": "token comment"
                }}>{`// apollo.config.js`}</span>{`
module`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`.`}</span>{`exports `}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`=`}</span>{` `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{`
  service`}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`:`}</span>{` `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{`
    `}<span parentName="code" {...{
                  "className": "token comment"
                }}>{`// Must match the name of your graph in Studio`}</span>{`
    name`}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`:`}</span>{` `}<span parentName="code" {...{
                  "className": "token string"
                }}>{`'docs-example-graph'`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`,`}</span>{`

    `}<span parentName="code" {...{
                  "className": "token comment"
                }}>{`// EITHER THIS`}</span>{`
    endpoint`}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`:`}</span>{` `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{`
      url`}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`:`}</span>{` `}<span parentName="code" {...{
                  "className": "token string"
                }}>{`"http://localhost:4000"`}</span>{`
    `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`}`}</span>{`
    `}<span parentName="code" {...{
                  "className": "token comment"
                }}>{`// OR THIS`}</span>{`
    localSchemaFile`}<span parentName="code" {...{
                  "className": "token operator"
                }}>{`:`}</span>{` `}<span parentName="code" {...{
                  "className": "token string"
                }}>{`'./path/to/schema.graphql'`}</span>{`
  `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`}`}</span>{`
`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`}`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`;`}</span></code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span></span></pre></div>
          <p>{`To register your schema with Studio, the Apollo CLI needs to know how to access the schema. You can do this by providing either a `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`service.endpoint`}</code>{` object or a `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`service.localSchemaFile`}</code>{` string in `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`apollo.config.js`}</code>{`.`}</p>
          <ul>
            <li parentName="ul">{`If you provide an `}<code parentName="li" {...{
                "className": "language-text"
              }}>{`endpoint`}</code>{` object, the CLI executes an introspection query on the GraphQL endpoint at the provided `}<code parentName="li" {...{
                "className": "language-text"
              }}>{`url`}</code>{` to obtain your schema. To use this method, introspection must be enabled on your server.`}</li>
            <li parentName="ul">{`If you provide a `}<code parentName="li" {...{
                "className": "language-text"
              }}>{`localSchemaFile`}</code>{` string, the CLI obtains your schema via the specified file path.`}</li>
          </ul>
          <p>{`The `}<a parentName="p" {...{
              "href": "https://www.apollographql.com/docs/references/apollo-config/"
            }}>{`Apollo config documentation`}</a>{` has more details and advanced configuration options for the `}<code parentName="p" {...{
              "className": "language-text"
            }}>{`apollo.config.js`}</code>{` format.`}</p>
        </ExpansionPanelListItem>
        <ExpansionPanelListItem number="check" mdxType="ExpansionPanelListItem">
  You're done!
        </ExpansionPanelListItem>
      </ExpansionPanelList>
    </ExpansionPanel>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      